import { useLanguage } from '../providers/preferenceProvider';
import { useEffect, useState } from 'react';
import '../css/account.css';
import { getUser, updateUserImage, updateUserName, updateUserEmail, deleteUser } from '../api/user-api';
import { useConfig } from '../providers/configProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-regular-svg-icons';
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import Overlay from '../shared/overlays/imageUpload';
import { useToast } from '../providers/toastProvider';
import { Link } from 'react-router-dom';
import { ApiClient } from '../api/api-client';

const Account = () => {
  const { t } = useLanguage();
  const [showOverlay, setShowOverlay] = useState(false);
  const { config, refresh } = useConfig();
  const [account, setAccount] = useState({});
  const [isEditingLastname, setIsEditingLastname] = useState(false);
  const [isEditingFirstname, setIsEditingFirstname] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [firstnameInput, setFirstnameInput] = useState('');
  const [lastnameInput, setLastnameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const { showToast, showActionToast, showLoading, hideLoading } = useToast();

  function closeOverlay() {
    setShowOverlay(false);
  }

  function openOverlay() {
    setShowOverlay(true);
  }

  useEffect(() => {
    getUser(config.user.id)
      .then((response) => {
        setAccount({
          id: response.data.user.id,
          firstname: response.data.user.firstname,
          lastname: response.data.user.lastname,
          email: response.data.user.email,
          image: response.data.user.image,
          personalCode: response.data.user.personalCode,
          createdAt: response.data.user.createdAt
        });

        setFirstnameInput(account.firstname);
        setLastnameInput(account.lastname);
      })
      .catch((error) => {
        console.error('Failed to fetch account:', error);
      });
  }, []);

  const editFirstname = () => {
    setFirstnameInput(account.firstname);
    setIsEditingFirstname(true);
    setIsEditingEmail(false);
  }

  const editLastname = () => {
    setLastnameInput(account.lastname);
    setIsEditingLastname(true);
    setIsEditingEmail(false);
  }

  const editEmail = () => {
    setEmailInput(account.email);
    setIsEditingEmail(true);
    setIsEditingFirstname(false);
    setIsEditingLastname(false);
  }

  const handleSaveName = async () => {
    showLoading();
    try {
      if (isEditingFirstname && !firstnameInput) {
        showToast(t.toast.fill_all_fields, 'error');
        hideLoading();
        return;
      }
      if (isEditingLastname && !lastnameInput) {
        showToast(t.toast.fill_all_fields, 'error');
        hideLoading();
        return;
      }
      if (isEditingFirstname && firstnameInput.length > 50) {
        showToast(t.toast.name_too_long, 'error');
        hideLoading();
        return;
      }
      if (isEditingLastname && lastnameInput.length > 50) {
        showToast(t.toast.name_too_long, 'error');
        hideLoading();
        return;
      }
      
      await updateUserName(config.user.id, account.email, firstnameInput, lastnameInput);
      refresh();
      setAccount(prev => ({ ...prev, firstname: firstnameInput ?? account.firstname, lastname: lastnameInput ?? account.lastname }));
      setIsEditingFirstname(false);
      setIsEditingLastname(false);
    } catch (error) {
      console.error('Error updating name:', error);
    };
    hideLoading();
  };

  const handleSaveEmail = async () => {
    showLoading();
    try {
      if (!emailInput || !emailInput.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        showToast(t.toast.fill_all_fields, 'error');
        return;
      }

      await updateUserEmail(config.user.id, emailInput);
      setAccount(prev => ({ ...prev, email: emailInput }));
      setIsEditingEmail(false);
    } catch (error) {
      showToast(t.server_error, 'error');
    }
    hideLoading();
  };

  const handleSaveImage = async (file) => {
    showLoading();
    try {
      await updateUserImage(config.user.id, file)
      closeOverlay();
      setAccount(prev => ({ ...prev, image: URL.createObjectURL(file) }));
      refresh();
    } catch (error) {
      console.error('Error updating image:', error);
    }
    hideLoading();
  }

  const deleteConfirmation = () => {
    showActionToast(
      t.toast.confirm_delete_general.replace('{ph_name}', account.firstname + ' ' + account.lastname),
      handleDeleteUser,
      null
    );
  }

  const handleDeleteUser = async () => {
    showLoading();
    try {
      await deleteUser(account.id);
      logout();
      showToast(t.user + " " + t.toast.deleted_success, 'success');
    } catch (error) {
      console.error('Error deleting user:', error);
    }
    hideLoading();
  }

  const logout = () => {
    ApiClient.getInstance().logoutUser().then(() => {
      sessionStorage.removeItem('userId');
      sessionStorage.removeItem('username');
      window.location.href = '/';
    }).catch((error) => {
      console.error('Logout failed:', error);
    });
  };

  return (
    <>
      <main className="main-container">
        <div className="main-box">
          <div className="main-header">{t.account}</div>
          <div className="account">
            <div className="account-image-wrapper md:ml-4">
              <img src={account.image} alt={t.account} className="account-image" />
              <button  onClick={openOverlay} className="edit-icon">
                <FontAwesomeIcon icon={faPenToSquare} />
              </button>
            </div>
            <div className="account-info md:ml-4">
              <div className="account-name gap-2 flex flex-row">
                {isEditingFirstname ? (
                  <>
                    <div className='flex flex-row'> 
                      <input type="text" value={firstnameInput} onChange={(e) => setFirstnameInput(e.target.value)} />
                      <button className="account-edit-button" onClick={handleSaveName}><FontAwesomeIcon icon={faCheck} /></button>
                      <button className="account-edit-button" onClick={() => setIsEditingFirstname(false)}><FontAwesomeIcon icon={faXmark} /></button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className='flex flex-row'> 
                      {account.firstname}
                      <button className="edit-icon-inline" onClick={editFirstname}>
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </button>
                    </div>
                  </>
                )}
                {isEditingLastname ? (
                  <>
                    <div className='flex flex-row'> 
                      <input type="text" value={lastnameInput} onChange={(e) => setLastnameInput(e.target.value)} />
                      <button className="account-edit-button" onClick={handleSaveName}><FontAwesomeIcon icon={faCheck} /></button>
                      <button className="account-edit-button" onClick={() => setIsEditingLastname(false)}><FontAwesomeIcon icon={faXmark} /></button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className='flex flex-row'> 
                      {account.lastname}
                      <button className="edit-icon-inline" onClick={editLastname}>
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div className="account-email">
                {isEditingEmail ? (
                  <>
                    <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} />
                    <button className="account-edit-button" onClick={handleSaveEmail}><FontAwesomeIcon icon={faCheck} /></button>
                    <button className="account-edit-button" onClick={() => setIsEditingEmail(false)}><FontAwesomeIcon icon={faXmark} /></button>
                  </>
                ) : (
                  <>
                    {account.email}
                    <button className="edit-icon-inline" onClick={editEmail}>
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                  </>
                )}
              </div>

              <div className="account-personal-code">
                {t.personal_code}: {account.personalCode}
              </div>
              <div className="account-created-at text-sm text-gray-500">
                {t.date_added}: {account.createdAt ? new Date(account.createdAt).toLocaleDateString() : ''}
              </div>
            </div>

            <div className="sub-header ">{t.actions}</div>
            <div className='flex flex-row gap-2 md:ml-4'>
              <Link to={`/forgot-password?email=${account.email}`}><button className='action-button'>{t.reset_password}</button></Link>
              <button onClick={deleteConfirmation} className='action-button'>{t.delete_account}</button>
            </div>
          </div>
        </div>
      </main>

      {showOverlay && (
        <Overlay isOpen={showOverlay} onClose={closeOverlay} image={account.image} handleSaveImage={handleSaveImage} />
      )}
    </>
  );
};

export default Account;